import { ScopeOption } from './first-clean-card.tsx';
import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { paymentApi, quoteApi } from '../../api/endpoints.ts';
import { quotePaymentsKey } from '../payments/quote-payments.tsx';
import { singleJobKey } from './single-job-card.tsx';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import {
  Alert,
  Button,
  Checkbox,
  ConfirmDialog,
  Select,
  SplitButton,
  StatusBadge,
  TextArea,
  TextInput,
} from '../ui.tsx';
import { PdfDownloadButton } from '../pdf-download.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { calculateRoomChargeCents, formatCents, groupSubtotals, reconcilePrice } from '@shared/calculations/pricing.ts';
import { PriceBreakdown, reconciliationHasGap, visibleGroupSubtotals } from './price-breakdown.tsx';
import { defaultGroupOf, groupsChromeVisible } from '@shared/quotes/groups.ts';
import { calculationInputFor } from '../../hooks/use-quote-editor.ts';
import { formatMinutes } from '../../i18n/format.ts';
import { conditionMultiplierFor, sizeMultiplierFor, SMS_QUOTE_NOTE_MAX } from '@shared/constants/index.ts';
import {
  canTransition,
  MANUAL_QUOTE_STATUSES,
  QUOTE_DECLINE_REASONS,
  type QuoteDeclineReason,
  type QuoteStatus,
} from '@shared/constants/index.ts';
import { isSamePhoneNumber } from '@shared/phone.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { repricingFrequencies } from '../../hooks/quote-merge.ts';
import { QuoteProfitWarning } from './quote-profit.tsx';
/*
  The customer's document, not the estimator's screen: this block previews the
  page and the PDF that will be sent, so its room and task names follow the
  QUOTE's language the way the Worker's renderer does — as does the rest of the
  editor, for the reason on `roomLabel` in rooms-step.tsx.
*/
import { localizedText } from '@shared/constants/localized-text.ts';
import type { Customer, Quote, QuoteRoom } from '@shared/types/index.ts';

/**
 * Step 6 (spec §16.1): customer preview, display toggles, terms, PDF, publish
 * and send.
 */
interface ReviewStepProps {
  quote: Quote;
  update: (mutate: (draft: Quote) => Quote) => void;
  currencyCode: string;
  readOnly: boolean;
  /** Flushes pending edits so an action never runs against a stale version. */
  save: () => Promise<Quote | null>;
  applyServerQuote: (quote: Quote, savedThrough?: number) => void;
  /**
   * The server's own copy, for the one thing this step reads off it: whether
   * each frequency tier's price still describes the discount on screen.
   *
   * The prices shown here are the customer's, so they are the Worker's
   * snapshots and never a local calculation — which means that between a
   * discount edit and the response that prices it, the tier holds the previous
   * price. `repricingFrequencies` finds those, and they read as being worked
   * out rather than as a number the customer could be quoted.
   */
  serverQuote?: Quote | null;
  savedEditCount: () => number;
  /** Holds the autosave while an action bumps the version; returns resume. */
  suspendAutosave: () => () => void;
  /**
   * The customer record behind the quote, when the editor has loaded it.
   *
   * Only the texting panel needs it, and only for one thing the quote's own
   * snapshot cannot answer: whether this customer has agreed to be texted, and
   * on which number. Consent is a live fact — a STOP arrives long after a quote
   * was built — so the button is offered or explained from the record rather
   * than from the quote.
   */
  customer?: Customer | null;
  /**
   * The quote's attachments, which belong to the quote rather than to a step
   * and are shown here because the review is where they are reachable.
   *
   * Passed in rather than rendered by the editor after this step, so that
   * "Finish" stays the last thing on the page: an action that ends the wizard
   * reads as premature with another section under it.
   */
  attachments?: ReactNode;
  /**
   * The job card above has taken over finishing the work (`single_job`, on a
   * plan with no calendar): "Mark completed" lives on the job's visit there,
   * so the quote-level button is not offered twice.
   */
  completedOnJob?: boolean;
  /**
   * The job is still to happen: it is canceled on its card, which closes the
   * visit and its payment link and tells the customer, so the quote-level
   * Cancel is not offered beside it.
   */
  cancelOnJob?: boolean;
}

/** What a room is charged, if it carries a price. Mirrors the public page. */
function roomChargeCents(room: QuoteRoom): number | null {
  return calculateRoomChargeCents({
    flatPriceCents: room.flatPriceCents,
    quantity: room.quantity,
    sizeMultiplierBasisPoints: sizeMultiplierFor(room.sizeCode),
    conditionMultiplierBasisPoints: conditionMultiplierFor(room.conditionCode),
  });
}

function visibleRooms(rooms: QuoteRoom[]): QuoteRoom[] {
  return rooms.filter((room) => {
    const visible = room.services.some((service) => service.isSelected && service.showOnCustomerQuote);
    return visible || Boolean(room.customerNotes) || roomChargeCents(room) !== null;
  });
}

/**
 * The customer preview. A residential quote with one untouched group stays a
 * flat room list. Once a group is named, counted or joined by another, each
 * group is a section with its own subtotal.
 */
function CustomerRoomList({ quote, money }: { quote: Quote; money: (cents: number) => string }) {
  const { t } = useTranslation();
  const groups = quote.groups ?? [];
  const chrome = groupsChromeVisible(groups);

  function roomItem(room: QuoteRoom) {
    const visible = room.services.filter((service) => service.isSelected && service.showOnCustomerQuote);
    const chargeCents = roomChargeCents(room);
    return (
      <li key={room.id}>
        <p className="font-medium">
          {localizedText(room.roomName, room.roomNameTranslations, quote.language)}
          {room.quantity > 1 ? ` ×${room.quantity}` : ''}
          {chargeCents !== null ? <span className="ml-2 text-sm font-semibold">{money(chargeCents)}</span> : null}
          {quote.showTimeOnQuote ? (
            <span className="ml-2 text-sm text-[color:var(--color-muted)]">{formatMinutes(room.billableMinutes)}</span>
          ) : null}
        </p>
        <ul className="mt-1 list-inside list-disc text-sm text-[color:var(--color-muted)]">
          {visible.map((service) => (
            <li key={service.id}>
              {localizedText(service.name, service.nameTranslations, quote.language)}
              {quote.showTimeOnQuote ? ` (${service.estimatedMinutes} min)` : ''}
            </li>
          ))}
        </ul>
        {room.customerNotes ? <p className="mt-1 text-sm">{room.customerNotes}</p> : null}
      </li>
    );
  }

  if (!chrome) {
    return <ul className="mb-4 space-y-3">{visibleRooms(quote.rooms).map(roomItem)}</ul>;
  }

  const fallback = defaultGroupOf(groups)?.id;
  const orderedGroups = [...groups].sort((left, right) => left.displayOrder - right.displayOrder);
  const partitions = orderedGroups.map((group) =>
    quote.rooms.flatMap((room, index) => {
      const groupId =
        room.groupId && groups.some((candidate) => candidate.id === room.groupId) ? room.groupId : fallback;
      return groupId === group.id ? [index] : [];
    }),
  );
  const contributions = groupSubtotals(calculationInputFor(quote), partitions);
  return (
    <div className="mb-4 space-y-4">
      {orderedGroups.map((group, groupIndex) => {
        const indexes = partitions[groupIndex] ?? [];
        const rooms = visibleRooms(indexes.map((index) => quote.rooms[index]).filter((room) => room !== undefined));
        const contribution = contributions[groupIndex] ?? { minutes: 0, cents: 0 };
        const subtotal = money(contribution.cents);
        const amount =
          quote.billingBasis === 'monthly' ? t('totalsPanel.perMonthAmount', { amount: subtotal }) : subtotal;
        return (
          <section key={group.id}>
            <h3 className="font-semibold">
              {(localizedText(group.name, group.nameTranslations, quote.language).trim() ||
                t('roomsStep.ungroupedName')) + (group.quantity > 1 ? ` ×${group.quantity}` : '')}
            </h3>
            {group.notes ? <p className="text-sm text-[color:var(--color-muted)]">{group.notes}</p> : null}
            <ul className="mt-2 space-y-3">{rooms.map(roomItem)}</ul>
            <p className="mt-2 text-sm font-semibold">
              {t('reviewStep.groupSubtotal', { amount })}
              {quote.showTimeOnQuote ? ` · ${formatMinutes(contribution.minutes)}` : ''}
            </p>
          </section>
        );
      })}
    </div>
  );
}

export default function ReviewStep({
  quote,
  update,
  currencyCode,
  readOnly,
  save,
  applyServerQuote,
  serverQuote,
  savedEditCount,
  suspendAutosave,
  customer,
  attachments,
  completedOnJob = false,
  cancelOnJob = false,
}: ReviewStepProps) {
  const { t } = useTranslation();
  const { can, company } = useSession();
  // See the schedule link below: a downgraded company keeps its old bookings
  // but cannot open the page that shows them.
  const hasScheduling = usePlanFeature('scheduling');
  // The same ledger the payments card above reads (one query, shared): money
  // on the quote is what a status change has to warn about, since the
  // payments stay where they are whatever the status becomes.
  const quoteLedger = useQuery({
    queryKey: quotePaymentsKey(quote.id),
    queryFn: () => paymentApi.quotePayments(quote.id),
    enabled: can('payments:read'),
  });
  const holdsPayments = (quoteLedger.data?.items ?? []).some(
    (payment) => payment.type === 'charge' && payment.status === 'succeeded',
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [overrideTo, setOverrideTo] = useState<QuoteStatus | ''>('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideDeclineReason, setOverrideDeclineReason] = useState<QuoteDeclineReason | ''>('');
  /**
   * A quote keeps the email it was created with. Adding one to the customer
   * afterwards does not change that snapshot, so the field starts from the
   * snapshot but stays editable — otherwise a quote created before the customer
   * had an address, or one going to a second address, is a dead end.
   */
  const [recipientEmail, setRecipientEmail] = useState(() => quote.customerEmailSnapshot ?? '');
  /** The note that travels at the top of the email, above the link. */
  const [emailMessage, setEmailMessage] = useState('');
  /** The texting panel's own pair, starting from the quote's phone snapshot. */
  const [recipientPhone, setRecipientPhone] = useState(() => quote.customerPhoneSnapshot ?? '');
  const [textMessage, setTextMessage] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  /** Open while staff confirm that a new link will turn off every older one. */
  const [confirmNewLink, setConfirmNewLink] = useState(false);
  /** Open while staff confirm that turning sharing off kills the link already sent. */
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);

  /**
   * Actions carry the quote version, so anything typed but not yet autosaved
   * has to land first (spec §11.1: autosave before generating a PDF or leaving
   * the editor).
   *
   * A failed save aborts the action rather than falling back to the last known
   * version: publishing or generating a PDF against the unsaved server copy
   * would put content in front of the customer that nobody chose to send.
   */
  /**
   * Edit count the server had when the running action started. Anything typed
   * after this is not in that action's response, so `applyServerQuote` must not
   * treat it as saved. A PDF render can take many seconds, which is plenty of
   * time to keep typing.
   */
  const savedWhenActionStarted = useRef(0);

  /**
   * The action also receives the saved quote, because the save can change what
   * the action should do: saving an edit to an accepted quote reopens it as a
   * draft (spec §22), and only the response says so. Deciding from the copy
   * this render captured would act on a status the server has already left.
   */
  async function withFreshVersion<T>(action: (version: number, saved: Quote) => Promise<T>): Promise<T | null> {
    // The action is about to bump the version server-side, and typing during
    // it would otherwise fire the debounced autosave with the pre-action
    // version — a conflict banner over a race nobody could see. Held for the
    // action's duration; resume pushes anything typed meanwhile.
    const resumeAutosave = suspendAutosave();
    try {
      const saved = await save();
      if (!saved) {
        setMessage({ tone: 'error', text: t('reviewStep.saveFirstError') });
        return null;
      }
      savedWhenActionStarted.current = savedEditCount();

      try {
        return await action(saved.version, saved);
      } catch (error) {
        setMessage({
          tone: 'error',
          text: error instanceof ApiRequestError ? error.message : t('reviewStep.genericActionError'),
        });
        return null;
      }
    } finally {
      resumeAutosave();
    }
  }

  /**
   * Adopts an action's response without discarding edits made while it ran.
   * A new status changes what the payments card offers (Record payment only
   * on an accepted quote, a warning once it moves on), so its ledger is read
   * again rather than kept from before.
   */
  const adopt = (result: Quote) => {
    applyServerQuote(result, savedWhenActionStarted.current);
    if (result.status !== quote.status) {
      void queryClient.invalidateQueries({ queryKey: quotePaymentsKey(quote.id) });
      // The job card above reads the status too: whether it can be booked,
      // and whether it can take a payment.
      void queryClient.invalidateQueries({ queryKey: singleJobKey(quote.id) });
      // And what is owed counts a quote by its status: accepted or completed.
      void queryClient.invalidateQueries({ queryKey: ['owed'] });
    }
  };

  const generatePdf = useMutation({
    mutationFn: () => withFreshVersion(() => quoteApi.generatePdf(quote.id)),
    onSuccess: async (result) => {
      if (!result) return;
      // The endpoint returns PDF metadata, not a quote, so the editor's copy
      // still says no PDF exists — refetch it or the download link stays hidden
      // until the page is reloaded.
      const refreshed = await quoteApi.get(quote.id).catch(() => null);
      if (refreshed) adopt(refreshed);
      // The list's own copy carries its own pdfAvailable, cached for 30s — see
      // `leaveForQuoteList` above. Without this, a PDF generated here and
      // followed by Back rather than Finish leaves the list showing no
      // download link until it happens to refetch.
      void queryClient.invalidateQueries({ queryKey: ['quotes'] });
      setMessage({ tone: 'success', text: t('reviewStep.pdfGeneratedSuccess') });
    },
  });

  const publish = useMutation({
    mutationFn: (regenerate: boolean) => withFreshVersion((version) => quoteApi.publish(quote.id, version, regenerate)),
    onSuccess: (result) => {
      setConfirmNewLink(false);
      if (!result) return;
      adopt(result.quote);
      setPublicUrl(result.publicUrl);
      setMessage({
        tone: 'success',
        text: result.publicUrl ? t('reviewStep.publishSuccessWithLink') : t('reviewStep.publishSuccessNoLink'),
      });
    },
  });

  const unpublish = useMutation({
    mutationFn: () => withFreshVersion((version) => quoteApi.unpublish(quote.id, version)),
    onSuccess: (result) => {
      setConfirmUnpublish(false);
      if (!result) return;
      adopt(result);
      setPublicUrl(null);
      setMessage({ tone: 'info', text: t('reviewStep.unpublishSuccess') });
    },
  });

  /** `finishing` is the send that ends the wizard, from the Finish menu. */
  const send = useMutation({
    mutationFn: (_finishing: boolean) =>
      withFreshVersion((version) =>
        quoteApi.send(quote.id, version, recipientEmail.trim() || undefined, emailMessage.trim() || undefined),
      ),
    onSuccess: (result, finishing) => {
      if (!result) return;
      adopt(result.quote);
      if (result.publicUrl) setPublicUrl(result.publicUrl);
      // The note belonged to the message that just went out; leaving it in the
      // box makes it look like it is still pending, and a second send would
      // repeat it.
      setEmailMessage('');

      if (finishing) {
        // No "copy the link below" here, unlike the message the panel shows:
        // there is no below to copy from once the list has taken over, and the
        // customer has the link in the email either way.
        leaveForQuoteList(
          t('reviewStep.finishNotice.emailed', { number: quote.quoteNumber, recipient: result.queuedFor }),
        );
        return;
      }

      setMessage({
        tone: 'success',
        text: result.publicUrl
          ? t('reviewStep.sentSuccessWithLink', { recipient: result.queuedFor })
          : t('reviewStep.sentSuccess', { recipient: result.queuedFor }),
      });
    },
  });

  /** The same send by text; `finishing` means the same thing it does above. */
  const sendText = useMutation({
    mutationFn: (_finishing: boolean) =>
      withFreshVersion((version) =>
        quoteApi.sendText(quote.id, version, recipientPhone.trim() || undefined, textMessage.trim() || undefined),
      ),
    onSuccess: (result, finishing) => {
      if (!result) return;
      adopt(result.quote);
      if (result.publicUrl) setPublicUrl(result.publicUrl);
      setTextMessage('');

      if (finishing) {
        leaveForQuoteList(
          t(result.deferredUntilOpenHours ? 'reviewStep.finishNotice.textDeferred' : 'reviewStep.finishNotice.texted', {
            number: quote.quoteNumber,
            recipient: result.queuedFor,
          }),
        );
        return;
      }

      // Quiet hours is not a failure: the quote is published and marked sent
      // either way, and the text is waiting to go out rather than lost. The
      // note may have been shortened to fit before it was ever queued, though
      // — that is still worth surfacing now, while there is time to email the
      // full version instead, not after the text has actually gone out.
      if (result.deferredUntilOpenHours) {
        setMessage({
          tone: 'info',
          text: t(result.noteTruncated ? 'reviewStep.textDeferredTruncated' : 'reviewStep.textDeferred', {
            recipient: result.queuedFor,
          }),
        });
        return;
      }

      // A text that the provider refused is the one outcome the email panel has
      // no equivalent of, and it is not a success: the quote is published and
      // marked sent, but nothing reached the customer, so the sender needs to
      // know to email it instead rather than to wait.
      if (!result.delivered) {
        setMessage({ tone: 'error', text: t('reviewStep.textNotDelivered', { recipient: result.queuedFor }) });
        return;
      }

      setMessage({
        tone: result.noteTruncated ? 'info' : 'success',
        text: result.noteTruncated
          ? t('reviewStep.textSentTruncated', { recipient: result.queuedFor, count: result.segments })
          : t('reviewStep.textSentSuccess', { recipient: result.queuedFor, count: result.segments }),
      });
    },
  });

  const markReady = useMutation({
    mutationFn: () => withFreshVersion((version) => quoteApi.markReady(quote.id, version)),
    onSuccess: (result) => {
      if (result) adopt(result);
    },
  });

  // Without these two, an accepted quote could never be marked finished and an
  // unwanted one could never be withdrawn — the endpoints existed but nothing
  // in the UI reached them.
  const complete = useMutation({
    mutationFn: () => withFreshVersion((version) => quoteApi.complete(quote.id, version)),
    onSuccess: (result) => {
      if (!result) return;
      adopt(result);
      setMessage({ tone: 'success', text: t('reviewStep.completeSuccess') });
    },
  });

  const cancel = useMutation({
    mutationFn: () => withFreshVersion((version) => quoteApi.cancel(quote.id, version)),
    onSuccess: (result) => {
      if (!result) return;
      adopt(result);
      setMessage({ tone: 'info', text: t('reviewStep.cancelSuccess') });
    },
  });

  const overrideStatus = useMutation({
    mutationFn: () =>
      withFreshVersion((version) =>
        quoteApi.overrideStatus(
          quote.id,
          version,
          overrideTo as QuoteStatus,
          overrideReason.trim(),
          // Only sent with a decline: the field is hidden for every other
          // target status, and a stale selection must not ride along.
          overrideTo === 'declined' ? overrideDeclineReason : undefined,
        ),
      ),
    onSuccess: (result) => {
      if (!result) return;
      adopt(result);
      setOverrideTo('');
      setOverrideReason('');
      setOverrideDeclineReason('');
      setMessage({
        tone: 'success',
        text: t('reviewStep.statusChangedSuccess', {
          status: t(`quotes.status.${result.status as QuoteStatus}`).toLowerCase(),
        }),
      });
    },
  });

  const status = quote.status as QuoteStatus;
  const money = (cents: number) => formatCents(cents, currencyCode, getDateLocale());
  /** Tiers whose stored price predates the discount on screen (see the prop). */
  const awaitingPrice = new Set(repricingFrequencies(quote, serverQuote));

  /**
   * Whether the quote can go out by email right now.
   *
   * A draft counts: sending one is the normal end of building a quote, and the
   * endpoint marks it ready on the way, so making somebody find "Mark as ready
   * to send" first was a step that decided nothing. Everything the customer has
   * already responded to is left out — the server refuses those transitions,
   * and offering a button that cannot work is worse than not offering one.
   */
  // `viewed` is a quote that was already sent and then opened. Resending it is
  // allowed; the server keeps the status and reuses the live link.
  const canEmail =
    status === 'sent' || status === 'viewed' || canTransition(status, 'sent') || canTransition(status, 'ready');
  const emailRecipient = recipientEmail.trim();

  /**
   * Whether the texting panel is worth showing at all.
   *
   * Two company-level facts, both of which the owner controls and neither of
   * which has anything to do with this quote: a Twilio account behind the
   * deployment or the company, and the text feature switched on in Settings. A
   * company with neither has no texting anywhere in the app, and a panel
   * explaining that on every quote would be noise.
   *
   * Consent is deliberately *not* part of this. It is per customer and per
   * number, and hiding the panel over it would leave "why can I email this
   * quote but not text it?" with nowhere to read the answer — so the panel
   * stays and the button carries the reason.
   */
  const companyCanText = Boolean(company?.smsConfigured) && Boolean(company?.smsRemindersEnabled);
  const canText = companyCanText && canEmail;
  const textRecipient = recipientPhone.trim();
  // Consent belongs to a line: an opted-in customer whose agreed number is not
  // the one typed here is not somebody this may text (the API refuses it too).
  const consentCoversNumber =
    customer?.smsConsentStatus === 'opted_in' &&
    isSamePhoneNumber(customer.smsConsentPhone, textRecipient, company?.countryCode ?? 'US');
  // Undefined while the customer is still loading — the button stays enabled
  // and the server has the final say, rather than flickering disabled.
  const textBlockedReason = !textRecipient
    ? t('reviewStep.textNoNumber')
    : customer && !consentCoversNumber
      ? t('reviewStep.textNoConsent')
      : // Only when the lookup actually answered about this number. Null means
        // nobody asked, which is not a reason to refuse a send.
        customer?.phoneLineType === 'landline'
        ? t('reviewStep.textLandline')
        : null;

  /**
   * Leaves the builder for the quote list, carrying what just happened.
   *
   * The list is 30 seconds stale by default, so without the invalidation the
   * quote that was just finished can be sitting there as a draft — the one row
   * the reader is looking for, showing the state they just changed.
   */
  function leaveForQuoteList(notice: string) {
    void queryClient.invalidateQueries({ queryKey: ['quotes'] });
    navigate('/quotes', { state: { notice } });
  }

  /**
   * The end of the wizard (spec §16.1).
   *
   * Finishing flushes the last edits and, for a quote still in draft, marks it
   * ready to send — the one status change that is purely internal, so it is
   * safe to fold into a button whose job is "I am done building this". Nothing
   * is closed off: the quote stays editable, and every route out of here ends
   * on the quote list, which is where the next piece of work is chosen from.
   */
  const finish = useMutation({
    mutationFn: async (then: 'quotes' | 'stay') => {
      const outcome = await withFreshVersion(async (version, saved) => {
        // Read off the saved copy, not this render's: see `withFreshVersion`.
        if (!canTransition(saved.status as QuoteStatus, 'ready')) return 'saved' as const;
        // An expired quote refuses every transition but cancellation, so
        // asking would fail the whole finish over a date that is fixed one
        // step back, on the customer step.
        if (saved.isExpired) return 'expired' as const;
        adopt(await quoteApi.markReady(quote.id, version));
        return 'readied' as const;
      });
      return outcome ? { then, outcome } : null;
    },
    onSuccess: (result) => {
      if (!result) return;
      const number = quote.quoteNumber;
      if (result.then === 'quotes') {
        leaveForQuoteList(t(`reviewStep.finishNotice.${result.outcome}`, { number }));
        return;
      }
      setMessage({
        // An expired quote is finished but cannot go anywhere, which is news
        // rather than good news.
        tone: result.outcome === 'expired' ? 'info' : 'success',
        text: t(`reviewStep.finishStayed.${result.outcome}`),
      });
    },
  });

  return (
    <div className="space-y-6">
      {message ? <Alert tone={message.tone === 'info' ? 'info' : message.tone}>{message.text}</Alert> : null}

      {/* Below the target margin, or a customer whose jobs keep running
          over: said before sending, never in the way of it (0035). */}
      {!readOnly ? <QuoteProfitWarning quote={quote} currencyCode={currencyCode} /> : null}

      {/* ---- What the customer sees ------------------------------------- */}
      <section className="card" aria-labelledby="preview-heading">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 id="preview-heading" className="text-lg font-semibold">
            {t('reviewStep.previewHeading')}
          </h2>
          <StatusBadge status={status} expired={quote.isExpired} quote={quote} />
        </div>

        <div className="mb-4 grid gap-x-6 sm:grid-cols-2">
          <Checkbox
            label={t('reviewStep.showEstimatedTime')}
            hint={t('reviewStep.showEstimatedTimeHint')}
            checked={quote.showTimeOnQuote}
            disabled={readOnly}
            onChange={(event) => update((draft) => ({ ...draft, showTimeOnQuote: event.target.checked }))}
          />
          <Checkbox
            label={t('reviewStep.showHourlyRate')}
            hint={t('reviewStep.showHourlyRateHint')}
            checked={quote.showHourlyRateOnQuote}
            disabled={readOnly}
            onChange={(event) => update((draft) => ({ ...draft, showHourlyRateOnQuote: event.target.checked }))}
          />
        </div>

        <div className="rounded-md border border-[color:var(--color-line)] p-4">
          <p className="mb-3 font-semibold">
            {quote.quoteNumber} · {quote.customerNameSnapshot}
          </p>
          {quote.firstClean ? (
            (quote.acceptedFirstClean ? [quote.acceptedFirstClean] : quote.firstClean.options).map((option) => (
              <ScopeOption key={option.id} option={option} currency={currencyCode} />
            ))
          ) : (
            // Folded, as the customer page is long: the total under it is what
            // the office checks before sending, and the rooms are a click away.
            <details className="mb-3">
              <summary className="min-h-11 cursor-pointer text-sm font-semibold">
                {t('reviewStep.showRoomsAndTasks')}
              </summary>
              <div className="mt-2">
                <CustomerRoomList quote={quote} money={money} />
              </div>
            </details>
          )}
          {!quote.firstClean &&
            (() => {
              const groupCents = visibleGroupSubtotals(quote);
              const lines = groupCents ? reconcilePrice(groupCents, quote.totals, quote.totals.totalCents) : null;
              if (lines && reconciliationHasGap(lines)) {
                const amount = (cents: number) =>
                  quote.billingBasis === 'monthly'
                    ? t('totalsPanel.perMonthAmount', { amount: money(cents) })
                    : money(cents);
                return <PriceBreakdown lines={lines} amount={amount} />;
              }
              return (
                <p className="text-lg font-bold">
                  {t(quote.billingBasis === 'monthly' ? 'reviewStep.totalPerMonth' : 'reviewStep.total', {
                    amount: money(quote.totals.totalCents),
                  })}
                </p>
              );
            })()}
          {/*
            The frequency options as the customer will read them. The figures
            are the server's own snapshots off the tier rows, which the
            autosave keeps current with the draft — so a tier whose discount
            has been edited since that snapshot says so, rather than showing
            the price of the discount before it.
          */}
          {quote.frequencyTiersEnabled && quote.frequencyTiers.length > 0 && !quote.acceptedFrequency ? (
            <ul className="mt-2 space-y-1 text-sm">
              {quote.frequencyTiers.map((tier) => (
                <li key={tier.frequency}>
                  {t(`schedule.frequency.${tier.frequency}`)} —{' '}
                  {awaitingPrice.has(tier.frequency)
                    ? t('reviewStep.tierRepricing')
                    : tier.frequency === 'one_time'
                      ? money(tier.totalCents)
                      : t('reviewStep.tierPerVisit', { amount: money(tier.totalCents) })}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      {/* ---- Notes and terms -------------------------------------------- */}
      <section className="card" aria-labelledby="terms-heading">
        <h2 id="terms-heading" className="mb-3 text-lg font-semibold">
          {t('reviewStep.termsHeading')}
        </h2>
        <TextArea
          label={t('reviewStep.noteToCustomer')}
          rows={3}
          value={quote.customerNotes ?? ''}
          disabled={readOnly}
          onChange={(event) => update((draft) => ({ ...draft, customerNotes: event.target.value || null }))}
        />
        {/* The terms start from the company's own and are seldom changed per
            quote, so six lines of them wait behind one. */}
        <details className="mb-2">
          <summary className="min-h-11 cursor-pointer font-semibold">{t('reviewStep.terms')}</summary>
          <div className="mt-2">
            <TextArea
              label={t('reviewStep.terms')}
              hint={t('reviewStep.termsHint')}
              rows={6}
              value={quote.termsSnapshot ?? ''}
              disabled={readOnly}
              onChange={(event) => update((draft) => ({ ...draft, termsSnapshot: event.target.value || null }))}
            />
          </div>
        </details>
        <TextArea
          label={t('reviewStep.internalNotes')}
          hint={t('reviewStep.internalNotesHint')}
          tone="internal"
          rows={2}
          value={quote.internalNotes ?? ''}
          disabled={readOnly}
          onChange={(event) => update((draft) => ({ ...draft, internalNotes: event.target.value || null }))}
        />
      </section>

      {/* Where this quote ended up. Above the acceptance note because it is
            the later fact and the more useful one: an accepted quote asks the
            office to book it, and this is the answer. The link is the whole
            point of the line — otherwise "it's already scheduled" leaves the
            reader searching the calendar for a customer name.

            Gated on the plan for the same reason the Schedule button is: a
            company that has downgraded away from scheduling still has its old
            bookings on file, and the series page answers such a visit with
            "your plan does not include this". A link to that is worse than no
            link. */}
      {quote.scheduledSeriesId && hasScheduling ? (
        <p className="mt-3 text-[color:var(--color-muted)]">
          {t('reviewStep.scheduledMessage')}{' '}
          <Link
            to={`/schedule/series/${quote.scheduledSeriesId}`}
            className="text-[color:var(--color-brand)] hover:underline"
          >
            {t('reviewStep.scheduledLink')}
          </Link>
        </p>
      ) : null}

      {status === 'accepted' ? (
        <p className="mt-3 text-[color:var(--color-muted)]">
          {quote.acceptedByName
            ? t('reviewStep.acceptedMessage', { name: quote.acceptedByName })
            : t('reviewStep.acceptedMessageNoName')}
          {/* The schedule the office now has to book — the choice the
                acceptance recorded, when the quote offered frequencies. */}
          {quote.acceptedFrequency
            ? ` ${t('reviewStep.acceptedFrequency', { frequency: t(`schedule.frequency.${quote.acceptedFrequency}`) })}`
            : ''}
        </p>
      ) : null}

      {holdsPayments ? (
        <p className="mt-3 text-sm text-[color:var(--color-warning)]">{t('reviewStep.paymentsStay')}</p>
      ) : null}

      {/* ---- PDF --------------------------------------------------------- */}
      <details className="space-y-4">
        <summary className="cursor-pointer py-2 font-semibold">{t('quotes.list.moreActions')}</summary>
        <section className="card" aria-labelledby="pdf-heading">
          <h2 id="pdf-heading" className="mb-3 text-lg font-semibold">
            {t('reviewStep.pdfHeading')}
          </h2>

          {quote.pdfAvailable && quote.pdfIsStale ? (
            <div className="mb-3">
              <Alert tone="warning">{t('reviewStep.pdfStale')}</Alert>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button busy={generatePdf.isPending} onClick={() => generatePdf.mutate()}>
              {quote.pdfAvailable ? t('reviewStep.regeneratePdf') : t('reviewStep.generatePdf')}
            </Button>
            {quote.pdfAvailable ? (
              <PdfDownloadButton url={quoteApi.pdfUrl(quote.id, { allowStale: true })}>
                {t('reviewStep.downloadPdf')}
              </PdfDownloadButton>
            ) : null}
            <Button onClick={() => window.print()}>{t('reviewStep.printPage')}</Button>
          </div>
        </section>

        {/* ---- Sharing ----------------------------------------------------- */}
        <section className="card" aria-labelledby="share-heading">
          <h2 id="share-heading" className="mb-3 text-lg font-semibold">
            {t('reviewStep.shareHeading')}
          </h2>

          {publicUrl ? (
            <div className="mb-4">
              <Alert tone="success" title={t('reviewStep.copyLinkNowTitle')}>
                {/*
                A read-only input rather than a <code> block: it is selectable
                with one tap on a phone, reads as a value to a screen reader,
                and gives back the exact string with no wrapping applied.
              */}
                <label className="field-label mt-2" htmlFor="customer-link">
                  {t('reviewStep.customerLinkLabel')}
                </label>
                <input
                  id="customer-link"
                  className="field-control font-mono text-xs"
                  readOnly
                  value={publicUrl}
                  onFocus={(event) => event.currentTarget.select()}
                />
                <Button className="mt-2" onClick={() => void navigator.clipboard?.writeText(publicUrl)}>
                  {t('common.copyLink')}
                </Button>
              </Alert>
            </div>
          ) : quote.publicLinkActive ? (
            <p className="mb-4 text-[color:var(--color-muted)]">{t('reviewStep.linkActiveMessage')}</p>
          ) : (
            <p className="mb-4 text-[color:var(--color-muted)]">{t('reviewStep.notPublishedMessage')}</p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button
              busy={publish.isPending}
              disabled={readOnly}
              onClick={() => {
                // A first link replaces nothing. A new one turns off every link
                // already in a customer's inbox, so that click has to say so
                // before it happens.
                if (quote.publicLinkActive) setConfirmNewLink(true);
                else publish.mutate(false);
              }}
            >
              {quote.publicLinkActive ? t('reviewStep.generateNewLink') : t('reviewStep.publishAndGetLink')}
            </Button>
            {quote.publicLinkActive ? (
              // Gated on the permission rather than on readOnly: a declined or
              // completed quote is read-only but its link still works, and
              // pulling it is exactly what somebody may want to do then.
              <Button
                variant="danger"
                busy={unpublish.isPending}
                disabled={!can('quotes:write')}
                onClick={() => setConfirmUnpublish(true)}
              >
                {t('reviewStep.turnOffSharing')}
              </Button>
            ) : null}
          </div>
          {confirmNewLink ? (
            <ConfirmDialog
              title={t('reviewStep.generateNewLinkTitle')}
              confirmLabel={t('reviewStep.generateNewLinkConfirm')}
              confirmVariant="danger"
              busy={publish.isPending}
              confirmDisabled={readOnly}
              onConfirm={() => publish.mutate(true)}
              onCancel={() => setConfirmNewLink(false)}
            >
              <p>{t('reviewStep.generateNewLinkWarning')}</p>
            </ConfirmDialog>
          ) : null}
          {confirmUnpublish ? (
            <ConfirmDialog
              title={t('reviewStep.turnOffSharingTitle')}
              confirmLabel={t('reviewStep.turnOffSharingConfirm')}
              confirmVariant="danger"
              initialFocus="cancel"
              busy={unpublish.isPending}
              onConfirm={() => unpublish.mutate()}
              onCancel={() => setConfirmUnpublish(false)}
            >
              <p>{t('reviewStep.turnOffSharingWarning')}</p>
            </ConfirmDialog>
          ) : null}
        </section>

        {/* ---- Email ------------------------------------------------------- */}
        {canEmail ? (
          <section className="card" aria-labelledby="email-heading">
            <h2 id="email-heading" className="mb-1 text-lg font-semibold">
              {t('reviewStep.emailHeading')}
            </h2>
            <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('reviewStep.emailDescription')}</p>

            {quote.sentAt ? (
              <p className="mb-4 text-sm text-[color:var(--color-muted)]">
                {t('reviewStep.lastEmailed', {
                  date: new Date(quote.sentAt).toLocaleString(getDateLocale()),
                })}
              </p>
            ) : null}

            {quote.pdfAvailable && quote.pdfIsStale ? (
              <div className="mb-4">
                <Alert tone="warning">{t('reviewStep.pdfStale')}</Alert>
              </div>
            ) : null}

            <div className="grid max-w-xl gap-1">
              <TextInput
                label={t('reviewStep.sendToLabel')}
                type="email"
                hint={t('reviewStep.sendToHint')}
                value={recipientEmail}
                disabled={readOnly}
                onChange={(event) => setRecipientEmail(event.target.value)}
              />
              <TextArea
                label={t('reviewStep.emailMessageLabel')}
                hint={t('reviewStep.emailMessageHint')}
                rows={3}
                maxLength={4000}
                value={emailMessage}
                disabled={readOnly}
                onChange={(event) => setEmailMessage(event.target.value)}
              />
              {/* Only ever an email: with no address this waits for one rather
                  than texting instead, which would drop the note typed above
                  and send the Text card's. Texting is that card's button. */}
              {!emailRecipient ? (
                <p className="text-sm text-[color:var(--color-muted)]">{t('reviewStep.emailNoAddress')}</p>
              ) : null}
              <div>
                <Button
                  variant="primary"
                  busy={send.isPending}
                  disabled={readOnly || !emailRecipient}
                  onClick={() => send.mutate(false)}
                >
                  {t('reviewStep.sendQuote')}
                </Button>
              </div>
            </div>
          </section>
        ) : null}

        {/* ---- Text -------------------------------------------------------- */}
        {canText ? (
          <section className="card" aria-labelledby="text-heading">
            <h2 id="text-heading" className="mb-1 text-lg font-semibold">
              {t('reviewStep.textHeading')}
            </h2>
            <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('reviewStep.textDescription')}</p>

            <div className="grid max-w-xl gap-1">
              <TextInput
                label={t('reviewStep.textToLabel')}
                type="tel"
                hint={t('reviewStep.textToHint')}
                value={recipientPhone}
                disabled={readOnly}
                onChange={(event) => setRecipientPhone(event.target.value)}
              />
              <TextArea
                label={t('reviewStep.textMessageLabel')}
                // The count is the honest half of the limit: the note is trimmed
                // against the *finished* message, whose length depends on a link
                // that does not exist yet, so the hint promises a trim rather
                // than a character budget it cannot compute.
                hint={t('reviewStep.textMessageHint', {
                  remaining: SMS_QUOTE_NOTE_MAX - textMessage.length,
                })}
                rows={2}
                maxLength={SMS_QUOTE_NOTE_MAX}
                value={textMessage}
                disabled={readOnly}
                onChange={(event) => setTextMessage(event.target.value)}
              />
              {textBlockedReason ? (
                <p className="text-sm text-[color:var(--color-muted)]">{textBlockedReason}</p>
              ) : null}
              <div>
                <Button
                  busy={sendText.isPending}
                  disabled={readOnly || Boolean(textBlockedReason)}
                  onClick={() => sendText.mutate(false)}
                >
                  {t('reviewStep.textInstead')}
                </Button>
              </div>
            </div>
          </section>
        ) : null}

        {/* ---- Status ------------------------------------------------------ */}
        <section className="card" aria-labelledby="status-heading">
          <h2 id="status-heading" className="mb-3 text-lg font-semibold">
            {t('reviewStep.statusHeading')}
          </h2>

          <div className="flex flex-wrap gap-3">
            {canTransition(status, 'ready') ? (
              <Button busy={markReady.isPending} disabled={readOnly} onClick={() => markReady.mutate()}>
                {t('reviewStep.markReady')}
              </Button>
            ) : null}

            {canTransition(status, 'completed') && !completedOnJob ? (
              <Button busy={complete.isPending} disabled={readOnly} onClick={() => complete.mutate()}>
                {t('reviewStep.markCompleted')}
              </Button>
            ) : null}

            {canTransition(status, 'cancelled') && !cancelOnJob ? (
              <Button
                variant="danger"
                busy={cancel.isPending}
                disabled={readOnly}
                onClick={() => {
                  if (window.confirm(t('reviewStep.confirmCancel'))) {
                    cancel.mutate();
                  }
                }}
              >
                {t('reviewStep.cancelQuote')}
              </Button>
            ) : null}
          </div>

          {/* ---- Manual override (quotes:status_override) -------------------
            Deliberately not gated on readOnly: a declined or canceled quote
            is read-only, and that is exactly the state an override exists to
            get out of. */}
          {can('quotes:status_override') ? (
            <div className="mt-6 border-t border-[color:var(--color-line)] pt-4">
              <h3 className="mb-1 font-semibold">{t('reviewStep.manualStatusHeading')}</h3>
              <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('reviewStep.manualStatusDescription')}</p>
              <div className="grid max-w-md gap-2">
                <Select
                  label={t('reviewStep.newStatusLabel')}
                  value={overrideTo}
                  onChange={(event) => setOverrideTo(event.target.value as QuoteStatus | '')}
                  options={[
                    { value: '', label: t('reviewStep.chooseStatusPlaceholder') },
                    ...MANUAL_QUOTE_STATUSES.filter((candidate) => candidate !== status).map((candidate) => ({
                      value: candidate,
                      label: t(`quotes.status.${candidate}`),
                    })),
                  ]}
                />
                <TextInput
                  label={t('reviewStep.reasonLabel')}
                  hint={t('reviewStep.reasonHint')}
                  value={overrideReason}
                  onChange={(event) => setOverrideReason(event.target.value)}
                />
                {/* Two different questions, which is why both are here: the
                  text above says why STAFF are changing the status, and this
                  says why the CUSTOMER said no. Only the second is countable,
                  and it is the one the funnel report reads. */}
                {overrideTo === 'declined' ? (
                  <Select
                    label={t('reviewStep.declineReasonLabel')}
                    hint={t('reviewStep.declineReasonHint')}
                    value={overrideDeclineReason}
                    onChange={(event) => setOverrideDeclineReason(event.target.value as QuoteDeclineReason | '')}
                    options={[
                      { value: '', label: t('reviewStep.declineReasonUnspecified') },
                      ...QUOTE_DECLINE_REASONS.map((candidate) => ({
                        value: candidate,
                        label: t(`reports.funnel.declineReason.${candidate}`),
                      })),
                    ]}
                  />
                ) : null}
                <div>
                  <Button
                    variant="danger"
                    busy={overrideStatus.isPending}
                    disabled={!overrideTo || !overrideReason.trim()}
                    onClick={() => {
                      const label = overrideTo ? t(`quotes.status.${overrideTo}`) : '';
                      const confirmation = t('reviewStep.confirmStatusChange', {
                        from: t(`quotes.status.${status}`),
                        to: label,
                      });
                      if (
                        window.confirm(
                          holdsPayments ? `${confirmation}\n\n${t('reviewStep.paymentsStay')}` : confirmation,
                        )
                      ) {
                        overrideStatus.mutate();
                      }
                    }}
                  >
                    {t('reviewStep.changeStatus')}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </section>
      </details>

      {attachments}

      {/* ---- Finish ------------------------------------------------------ */}
      {readOnly ? null : (
        <section className="card" aria-labelledby="finish-heading">
          <h2 id="finish-heading" className="mb-1 text-lg font-semibold">
            {t('reviewStep.finishHeading')}
          </h2>
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">
            {canTransition(status, 'ready')
              ? t('reviewStep.finishDescriptionDraft')
              : t('reviewStep.finishDescription')}
          </p>
          <SplitButton
            label={t('reviewStep.finish')}
            menuLabel={t('reviewStep.finishMenuLabel')}
            variant="secondary"
            busy={finish.isPending || send.isPending || sendText.isPending}
            onClick={() => finish.mutate('quotes')}
            actions={[
              {
                key: 'email',
                label: t('reviewStep.finishAndEmail'),
                // The address and note from the email section above travel with
                // it, so the choice made there is the one that goes out.
                hint: emailRecipient
                  ? t('reviewStep.finishAndEmailHint', { recipient: emailRecipient })
                  : t('reviewStep.finishAndEmailNoRecipient'),
                disabled: !canEmail || !emailRecipient,
                onSelect: () => send.mutate(true),
              },
              // Offered only where texting is set up at all, rather than shown
              // permanently disabled: a company that does not text has no use
              // for a menu entry it can never pick.
              ...(canText
                ? [
                    {
                      key: 'text',
                      label: t('reviewStep.finishAndText'),
                      hint: textBlockedReason ?? t('reviewStep.finishAndTextHint', { recipient: textRecipient }),
                      disabled: Boolean(textBlockedReason),
                      onSelect: () => sendText.mutate(true),
                    },
                  ]
                : []),
              {
                key: 'stay',
                label: t('reviewStep.finishAndStay'),
                hint: t('reviewStep.finishAndStayHint'),
                onSelect: () => finish.mutate('stay'),
              },
            ]}
          />
        </section>
      )}
    </div>
  );
}
